import * as THREE from 'three';
import maplibregl from 'maplibre-gl';
import { PilotData } from '../../types';
import { ModelLoader } from './pilot3d/ModelLoader';

export class Pilot3DLayer {
  private id: string;
  private camera: THREE.Camera;
  private scene: THREE.Scene;
  private renderer: THREE.WebGLRenderer;
  private map: maplibregl.Map | null = null;
  private modelLoader: ModelLoader;
  private pilots: Map<string, PilotData> = new Map();
  private pilotMeshes: Map<string, THREE.Group> = new Map();
  private isReady: boolean = false;
  private labelsAdded: boolean = false;
  private renderCount: number = 0;
  private pilotLabels: Map<string, THREE.Mesh> = new Map();
  private pilotTracks: Map<string, THREE.Mesh> = new Map();
  private trackPoints: Map<string, THREE.Vector3[]> = new Map();
  private maxTrackPoints: number = 500; // Keep last 500 points for detailed thermal tracking
  private pilotSpheres: Map<string, THREE.Mesh> = new Map();

  constructor(id: string) {
    this.id = id;
    this.camera = new THREE.Camera();
    this.scene = new THREE.Scene();
    this.renderer = new THREE.WebGLRenderer();
    this.modelLoader = ModelLoader.getInstance();

    // Lighting
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(1, 1, 1).normalize();
    this.scene.add(dirLight);

    // Load model and update existing pilots when ready
    this.modelLoader.loadParagliderModel().then(() => {
      // Model loaded, update any existing pilots that are using boxes
      this.pilotMeshes.forEach((mesh, pilotId) => {
        const pilot = this.pilots.get(pilotId);
        if (pilot && mesh.children.length > 0) {
          // Check if using a box (has BoxGeometry)
          const firstChild = mesh.children[0];
          if (firstChild && (firstChild as any).geometry && (firstChild as any).geometry.type === 'BoxGeometry') {
            // Remove the box
            mesh.remove(firstChild);
            (firstChild as any).geometry.dispose();
            if ((firstChild as any).material) (firstChild as any).material.dispose();

            // Add the model
            const model = this.modelLoader.cloneModel();
            if (model) {
              this.modelLoader.applyColorToModel(model, pilot.color || '#FF0000');
              mesh.add(model);
            }
          }
        }
      });
      if (this.map) this.map.triggerRepaint();
    }).catch(() => {});
  }

  private updatePilotTrack(pilotId: string, coord: maplibregl.MercatorCoordinate, color: string): void {
    let points = this.trackPoints.get(pilotId);
    if (!points) {
      points = [];
      this.trackPoints.set(pilotId, points);
    }

    // Track should be anchored at the exact bottom center of the model
    // No offset needed - use exact pilot position
    const newPoint = new THREE.Vector3(coord.x, coord.y, coord.z || 0);

    // Only add point if it's different from the last one (pilot moved)
    // Use smaller threshold to capture more detail in thermals
    if (points.length === 0 ||
        points[points.length - 1].distanceTo(newPoint) > 0.00000001) {

      points.push(newPoint);

      // Limit track length
      if (points.length > this.maxTrackPoints) {
        points.shift();
      }

      // Update or create track tube
      if (points.length >= 2) {
        let track = this.pilotTracks.get(pilotId);

        // Create a smooth curve from the points
        // Set tension to 0.5 for smoother curves through thermal circles
        const curve = new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.5);

        // Create tube geometry - thin but visible
        const tubeRadius = 0.0000003; // Thinner tube
        // More segments for smoother curves, especially in thermals
        const tubularSegments = Math.max(50, points.length * 3);
        const radialSegments = 6; // Enough segments for smooth tube

        if (track) {
          // Update existing track geometry
          const tubeGeometry = new THREE.TubeGeometry(
            curve,
            tubularSegments,
            tubeRadius,
            radialSegments,
            false
          );
          track.geometry.dispose();
          track.geometry = tubeGeometry;
        } else {
          // Create new track with bright, glowing appearance
          const tubeGeometry = new THREE.TubeGeometry(
            curve,
            tubularSegments,
            tubeRadius,
            radialSegments,
            false
          );

          // Parse pilot color and make it VERY bright and saturated
          const trackColor = new THREE.Color(color);
          const hsl = { h: 0, s: 0, l: 0 };
          trackColor.getHSL(hsl);
          // Maximum saturation and high lightness for neon effect
          trackColor.setHSL(hsl.h, 1.0, 0.75);

          // Use emissive material for glow effect
          const material = new THREE.MeshBasicMaterial({
            color: trackColor,
            transparent: true,
            opacity: 0.6, // Semi-transparent for trail effect
            side: THREE.DoubleSide,
            depthWrite: false,
            depthTest: false // Ensure track is always visible
          });

          // Make it glow by avoiding tone mapping
          material.toneMapped = false;

          track = new THREE.Mesh(tubeGeometry, material);
          track.renderOrder = 500; // Render below labels but above terrain

          this.scene.add(track);
          this.pilotTracks.set(pilotId, track);

        }
      }
    }
  }

  private updateLabelContent(labelMesh: THREE.Mesh, pilot: PilotData, agl?: number, terrainHeight?: number): void {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 90;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      // Professional dark semi-transparent background with rounded corners
      ctx.fillStyle = 'rgba(20, 24, 33, 0.85)';
      const radius = 10;
      ctx.beginPath();
      ctx.moveTo(radius, 0);
      ctx.lineTo(400 - radius, 0);
      ctx.quadraticCurveTo(400, 0, 400, radius);
      ctx.lineTo(400, 90 - radius);
      ctx.quadraticCurveTo(400, 90, 400 - radius, 90);
      ctx.lineTo(radius, 90);
      ctx.quadraticCurveTo(0, 90, 0, 90 - radius);
      ctx.lineTo(0, radius);
      ctx.quadraticCurveTo(0, 0, radius, 0);
      ctx.closePath();
      ctx.fill();

      // Add subtle border
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Pilot name - smaller font
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 24px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
      ctx.shadowBlur = 3;
      ctx.fillText(pilot.name || 'Unknown', 200, 30);

      // Reset shadow for next text
      ctx.shadowBlur = 0;

      // Stats row with smaller font
      ctx.font = '18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

      // Speed with icon-like indicator
      const speed = Math.round(pilot.speed || 0);
      ctx.fillStyle = '#4AE896';
      ctx.fillText(`▶ ${speed} km/h`, 65, 60);

      // MSL Altitude - pilot.altitude is already MSL
      const altitude = Math.round(pilot.altitude || 0);
      ctx.fillStyle = '#6BB6FF';
      ctx.fillText(`▲ ${altitude}m MSL`, 175, 60);

      // AGL if available - show 0 if negative (on ground)
      if (agl !== undefined && !isNaN(agl)) {
        const aglRounded = Math.max(0, Math.round(agl));  // Show 0 instead of negative
        ctx.fillStyle = '#FFB86C';
        ctx.fillText(`↕ ${aglRounded}m AGL`, 290, 60);
      }
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    const material = labelMesh.material as THREE.MeshBasicMaterial;
    if (material.map) {
      material.map.dispose();
    }
    material.map = texture;
    material.needsUpdate = true;
  }

  async updatePilots(pilots: Map<string, PilotData>): Promise<void> {
    this.pilots = pilots;

    // Skip if 3D layer not ready
    if (!this.isReady) {
      return;
    }

    // Remove old pilots
    const currentIds = new Set(Array.from(pilots.keys()));
    this.pilotMeshes.forEach((mesh, id) => {
      if (!currentIds.has(id)) {
        this.scene.remove(mesh);
        this.pilotMeshes.delete(id);

        // Clean up label mesh
        const label = this.pilotLabels.get(id);
        if (label) {
          this.scene.remove(label);
          label.geometry.dispose();
          const labelMat = label.material as THREE.MeshBasicMaterial;
          if (labelMat.map) labelMat.map.dispose();
          labelMat.dispose();
          this.pilotLabels.delete(id);
        }

        // Clean up track
        const track = this.pilotTracks.get(id);
        if (track) {
          this.scene.remove(track);
          track.geometry.dispose();
          (track.material as THREE.Material).dispose();
          this.pilotTracks.delete(id);
        }
        this.trackPoints.delete(id);

        // Clean up sphere
        const sphere = this.pilotSpheres.get(id);
        if (sphere) {
          this.scene.remove(sphere);
          sphere.geometry.dispose();
          (sphere.material as THREE.Material).dispose();
          this.pilotSpheres.delete(id);
        }

      }
    });

    // Add/update pilots AND create labels immediately
    pilots.forEach((pilot) => {
      let mesh = this.pilotMeshes.get(pilot.id);
      if (!mesh) {
        mesh = new THREE.Group();

        // Add the 3D model or box
        if (this.modelLoader.isLoaded()) {
          const model = this.modelLoader.cloneModel();
          if (model) {
            this.modelLoader.applyColorToModel(model, pilot.color || '#FF0000');
            mesh.add(model);
          }
        } else {
          const box = new THREE.Mesh(
            new THREE.BoxGeometry(0.000004, 0.000004, 0.000002),
            new THREE.MeshBasicMaterial({ color: pilot.color || '#FF0000' })
          );
          mesh.add(box);
        }

        this.scene.add(mesh);
        this.pilotMeshes.set(pilot.id, mesh);

        // Create professional label plane if it doesn't exist
        if (!this.pilotLabels.has(pilot.id)) {
          const canvas = document.createElement('canvas');
          canvas.width = 400;
          canvas.height = 90;
          const ctx = canvas.getContext('2d');

          if (ctx) {
            // Professional dark semi-transparent background with rounded corners
            ctx.fillStyle = 'rgba(20, 24, 33, 0.9)';
            const radius = 12;
            ctx.beginPath();
            ctx.moveTo(radius, 0);
            ctx.lineTo(512 - radius, 0);
            ctx.quadraticCurveTo(512, 0, 512, radius);
            ctx.lineTo(512, 128 - radius);
            ctx.quadraticCurveTo(512, 128, 512 - radius, 128);
            ctx.lineTo(radius, 128);
            ctx.quadraticCurveTo(0, 128, 0, 128 - radius);
            ctx.lineTo(0, radius);
            ctx.quadraticCurveTo(0, 0, radius, 0);
            ctx.closePath();
            ctx.fill();

            // Add subtle border
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
            ctx.lineWidth = 2;
            ctx.stroke();

            // Pilot name - large and clear
            ctx.fillStyle = '#FFFFFF';
            ctx.font = 'bold 42px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
            ctx.textAlign = 'center';
            ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
            ctx.shadowBlur = 4;
            ctx.fillText(pilot.name || 'Unknown', 256, 48);

            // Reset shadow for next text
            ctx.shadowBlur = 0;

            // Stats row with better spacing
            ctx.font = '28px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

            // Speed with icon-like indicator
            const speed = Math.round(pilot.speed || 0);
            ctx.fillStyle = '#4AE896';
            ctx.fillText(`▶ ${speed} km/h`, 128, 90);

            // Separator
            ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
            ctx.fillText('•', 256, 90);

            // Altitude with icon
            const altitude = Math.round(pilot.altitude || 0);
            ctx.fillStyle = '#6BB6FF';
            ctx.fillText(`▲ ${altitude}m`, 384, 90);
          }

          const texture = new THREE.CanvasTexture(canvas);
          texture.needsUpdate = true;

          // Create plane mesh instead of sprite - adjusted for 400x90 aspect ratio (4.44:1)
          const planeGeometry = new THREE.PlaneGeometry(0.000008, 0.0000018); // Smaller size, proper aspect
          const planeMaterial = new THREE.MeshBasicMaterial({
            map: texture,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 1.0,
            depthTest: false,
            depthWrite: false
          });

          const labelPlane = new THREE.Mesh(planeGeometry, planeMaterial);
          labelPlane.renderOrder = 1000; // Render on top

          // Add to scene immediately
          this.scene.add(labelPlane);
          this.pilotLabels.set(pilot.id, labelPlane);
        }

        // Initialize track points for new pilot
        // Don't create initial point here - let it be created in render loop
        if (!this.trackPoints.has(pilot.id)) {
          this.trackPoints.set(pilot.id, []);
        }

        // Create red sphere for this pilot
        if (!this.pilotSpheres.has(pilot.id)) {
          const sphereRadius = 0.0000003; // Very small sphere radius in mercator units
          const sphereGeometry = new THREE.SphereGeometry(sphereRadius, 16, 16);
          const sphereMaterial = new THREE.MeshBasicMaterial({
            color: 0xff0000, // Red color
            transparent: true,
            opacity: 0.8,
            depthTest: false,
            depthWrite: false
          });
          const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
          sphere.renderOrder = 900; // Render below labels but above tracks
          this.scene.add(sphere);
          this.pilotSpheres.set(pilot.id, sphere);
        }
      }
      mesh.userData = { ...pilot };

      // Update label content if pilot data changed (without AGL as we don't have terrain data here)
      const label = this.pilotLabels.get(pilot.id);
      if (label) {
        this.updateLabelContent(label, pilot);
      }
    });

    if (this.map) this.map.triggerRepaint();
  }

  getLayer() {
    const self = this;
    return {
      id: this.id,
      type: 'custom' as const,
      renderingMode: '3d' as const,

      onAdd(map: maplibregl.Map, gl: WebGLRenderingContext) {
        self.map = map;
        self.renderer = new THREE.WebGLRenderer({
          canvas: map.getCanvas(),
          context: gl,
          antialias: true,
          alpha: true
        });
        self.renderer.autoClear = false;

        // CRITICAL: Reset the renderer state immediately to ensure clean context
        self.renderer.resetState();

        // Mark as ready RIGHT NOW
        self.isReady = true;

        // Process pilots immediately
        if (self.pilots.size > 0) {
          self.updatePilots(self.pilots);
        }

        // Force multiple repaints to ensure objects are rendered
        map.triggerRepaint();
        requestAnimationFrame(() => {
          map.triggerRepaint();
          setTimeout(() => map.triggerRepaint(), 50);
        });
      },

      render(_gl: WebGLRenderingContext, matrix: any) {
        if (!self.map || !self.renderer) return;

        // Don't render until ready
        if (!self.isReady) {
          if (Math.random() < 0.01) {
          }
          return;
        }

        // Log first render
        if (!self.renderCount) {
          self.renderCount = 1;
        }

        const projMatrix = Array.isArray(matrix) ? matrix :
          (matrix.defaultProjectionData?.mainMatrix || matrix.projectionMatrix || matrix);
        self.camera.projectionMatrix = new THREE.Matrix4().fromArray(projMatrix);

        self.pilotMeshes.forEach((mesh, id) => {
          const pilot = self.pilots.get(id);
          if (!pilot) return;

          // Get terrain elevation at pilot position
          let terrainElevation = 0;

          if (self.map) {
            // In MapLibre v5, queryTerrainElevation returns meters above sea level directly
            if (typeof (self.map as any).queryTerrainElevation === 'function') {
              const rawElevation = (self.map as any).queryTerrainElevation([pilot.lng, pilot.lat]);

              if (rawElevation !== null && rawElevation !== undefined && !isNaN(rawElevation)) {
                // v5 returns elevation in meters above sea level
                terrainElevation = rawElevation;
              }
            }

            // Fallback: Try to get elevation from the terrain object
            if (terrainElevation === 0 && self.map.terrain) {
              // The terrain should have the elevation data
              // Try zoom level 14 which is typical for terrain tiles
              const elevResult = self.map.terrain.getElevationForLngLatZoom(
                new maplibregl.LngLat(pilot.lng, pilot.lat),
                14
              );

              if (elevResult && elevResult !== 0) {
                // The result is likely already in meters since we're using Terrarium encoding
                terrainElevation = elevResult;
              }
            }

            // Method 2: If still no valid elevation, try querying the map for elevation data
            if ((terrainElevation === 0 || terrainElevation < -100) && self.map) {
              // Project the coordinates to screen space
              const point = self.map.project([pilot.lng, pilot.lat]);

              // Query features at that point
              const features = self.map.queryRenderedFeatures(point);

              // Look for elevation in feature properties
              for (const feature of features) {
                if (feature.properties) {
                  // Common elevation property names
                  const elevProps = ['elevation', 'ele', 'height', 'altitude', 'elev', 'z'];
                  for (const prop of elevProps) {
                    if (feature.properties[prop] !== undefined) {
                      const elev = parseFloat(feature.properties[prop]);
                      if (!isNaN(elev) && elev > -100 && elev < 9000) {
                        terrainElevation = elev;
                        break;
                      }
                    }
                  }
                  if (terrainElevation !== 0) break;
                }
              }
            }

            // Last resort: check the transform for terrain data
            if (terrainElevation === 0 && (self.map as any).transform) {
              const transform = (self.map as any).transform;
              if (transform.elevation) {
                // Try to get elevation from transform
                const point = self.map.project([pilot.lng, pilot.lat]);
                const elevation = transform.elevation.getAtPoint(point);
                if (elevation !== undefined && elevation !== null) {
                  // The elevation from transform might be in different units or reference
                  // If it's negative and large, it might be relative to something else
                  if (elevation < -100) {
                    // This might be in a different coordinate system
                    // Try to get the absolute elevation
                    const metersPerPixel = transform.scale;
                    terrainElevation = Math.abs(elevation) * metersPerPixel;
                  } else {
                    terrainElevation = elevation;
                  }
                }
              }
            }

          }

          // CORRECTION: pilot.altitude is MSL, not AGL!
          // The actual ground elevation from Google Earth doesn't match our terrain service
          // but we'll calculate AGL = MSL - terrain elevation
          const pilotAltitudeMSL = pilot.altitude || 0;
          const altitudeAGL = pilotAltitudeMSL - terrainElevation;

          // Log coordinates for Google Earth verification
          console.log(`[PILOT DATA] ${pilot.name}:
            Lat: ${pilot.lat}, Lng: ${pilot.lng}
            RAW pilot.altitude (MSL): ${pilot.altitude}m
            Terrain Elevation (from service): ${terrainElevation.toFixed(1)}m
            Calculated AGL: ${altitudeAGL.toFixed(1)}m
            Note: If AGL is negative, pilot MSL is below terrain estimate
            Google Earth Link: https://earth.google.com/web/@${pilot.lat},${pilot.lng},${pilotAltitudeMSL}a,500d,35y,0h,0t,0r`);



          // Create mercator coordinate at pilot's actual MSL altitude
          const coord = maplibregl.MercatorCoordinate.fromLngLat([pilot.lng, pilot.lat], pilotAltitudeMSL);

          // Position model at its MSL altitude
          mesh.position.set(coord.x, coord.y, coord.z || 0);
          if (pilot.heading !== undefined) mesh.rotation.z = -pilot.heading * Math.PI / 180;

          // Update track points - account for any internal model offset
          // The model might have internal positioning, so get its world position
          mesh.updateMatrixWorld(true);
          const worldPos = new THREE.Vector3();
          mesh.getWorldPosition(worldPos);

          // Create a coord-like object with the actual world position
          const trackCoord = {
            x: worldPos.x,
            y: worldPos.y,
            z: worldPos.z
          } as maplibregl.MercatorCoordinate;

          self.updatePilotTrack(id, trackCoord, pilot.color || '#FF0000');

          // Update red sphere position
          const sphere = self.pilotSpheres.get(id);
          if (sphere) {
            // Position sphere exactly at pilot's current position
            sphere.position.set(coord.x, coord.y, coord.z || 0);
            sphere.visible = true;
          }

          // Green vertical line removed - positioning verified to be working correctly

          // Update label plane position and rotation to face camera
          const label = self.pilotLabels.get(id);
          if (label) {
            // Update label content with AGL and terrain information
            self.updateLabelContent(label, pilot, altitudeAGL, terrainElevation);

            // Position label directly above the pilot model
            // The bottom edge of the label should sit on top of the glider
            // Need offset to place above the 3D model (in mercator units)
            const labelOffsetMeters = 80; // 80 meters above the pilot (increased from 50)
            const mercatorScale = coord.meterInMercatorCoordinateUnits();
            const labelOffsetMercator = labelOffsetMeters * mercatorScale;

            label.position.set(
              coord.x,
              coord.y,
              (coord.z || 0) + labelOffsetMercator
            );

            // Make the label face the camera (billboard effect)
            // The plane should stand upright and face forward
            label.rotation.x = Math.PI / 2;  // Stand perpendicular to ground (original orientation was correct)
            label.rotation.y = 0;  // Face forward
            label.rotation.z = 0;  // No roll

            // Ensure label is visible
            label.visible = true;

            // Log occasionally to debug
            if (Math.random() < 0.01) {
            }
          }
        });

        // Log scene state periodically
        if (Math.random() < 0.02) {
        }

        self.renderer.resetState();
        self.renderer.render(self.scene, self.camera);
        self.map.triggerRepaint();
      },

      onRemove() {
        self.isReady = false;
        self.map = null;
      }
    };
  }

  setVisibility(visible: boolean): void {
    this.scene.visible = visible;
    if (this.map) this.map.triggerRepaint();
  }

  dispose(): void {
    // Clean up all tracks
    this.pilotTracks.forEach((track) => {
      this.scene.remove(track);
      track.geometry.dispose();
      (track.material as THREE.Material).dispose();
    });
    this.pilotTracks.clear();
    this.trackPoints.clear();

    // Clean up labels
    this.pilotLabels.forEach((label) => {
      this.scene.remove(label);
      label.geometry.dispose();
      const mat = label.material as THREE.MeshBasicMaterial;
      if (mat.map) mat.map.dispose();
      mat.dispose();
    });
    this.pilotLabels.clear();

    // Clean up spheres
    this.pilotSpheres.forEach((sphere) => {
      this.scene.remove(sphere);
      sphere.geometry.dispose();
      (sphere.material as THREE.Material).dispose();
    });
    this.pilotSpheres.clear();

    this.scene.clear();
    this.pilotMeshes.clear();
    if (this.renderer) this.renderer.dispose();
  }
}